import { useState, FormEvent } from 'react'
import { Button } from '@gjl/ui'

interface ContactFormProps {
  header?: string
  body?: string
  className?: string
  buttonVariant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive'
}

export function ContactForm({
  header = "Let's Connect",
  body = 'Have a question or want to work together? Send a message below.',
  className = '',
  buttonVariant = 'default',
}: ContactFormProps) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [nickname, setNickname] = useState('') // honeypot
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const [error, setError] = useState('')

  const apiUrl = import.meta.env.VITE_API_URL || ''

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()

    // Honeypot check
    if (nickname) {
      setStatus('success')
      return
    }

    setStatus('sending')
    setError('')

    try {
      const response = await fetch(`${apiUrl}/api/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: 'garrett@garrettjohnlaw.com',
          subject: `[Software Site] Contact from ${name}`,
          text: `From: ${name}\nEmail: ${email}\n\n${message}`,
          nickname, // honeypot
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to send message')
      }

      setStatus('success')
      setName('')
      setEmail('')
      setMessage('')
    } catch (err) {
      setStatus('error')
      setError((err as Error).message)
    }
  }

  if (status === 'success') {
    return (
      <div className={`mx-auto max-w-md space-y-4 text-center ${className}`}>
        <p className="text-lg font-semibold text-green-400">Thanks! Your message was sent.</p>
        <a
          href="https://www.linkedin.com/in/garrettjohnlaw"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-primary text-white underline"
        >
          Connect with Garrett on LinkedIn
        </a>
      </div>
    )
  }

  return (
    <div className={`mx-auto max-w-md space-y-6 ${className}`}>
      <div className="space-y-2 text-center">
        <h2 className="text-2xl font-bold text-white">{header}</h2>
        <p className="text-muted-foreground">{body}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Honeypot field - hidden from users */}
        <input
          type="text"
          name="nickname"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          className="hidden"
          tabIndex={-1}
          autoComplete="off"
        />

        <div>
          <label htmlFor="name" className="mb-1 block text-sm font-medium text-white">
            Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="focus:ring-primary w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-white focus:ring-2 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-white">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="focus:ring-primary w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-white focus:ring-2 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="message" className="mb-1 block text-sm font-medium text-white">
            Message
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={4}
            className="focus:ring-primary w-full resize-none rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-white focus:ring-2 focus:outline-none"
          />
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <Button type="submit" variant={buttonVariant} disabled={status === 'sending'} className="w-full">
          {status === 'sending' ? 'Sending...' : 'Send Message'}
        </Button>
      </form>
    </div>
  )
}
